import React from 'react'
import {Link} from 'react-router-dom'
import {motion} from 'framer-motion'
import Layout from '../../components/layout/Layout'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'

const {FiShield, FiArrowLeft, FiEye, FiLock, FiDatabase, FiUsers, FiMail} = FiIcons

const PrivacyPolicyPage = () => {
  return (
    <Layout showNavbar={false}>
      <div className="min-h-screen bg-gradient-to-br from-neutral-bg via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-500 mb-6">
              <SafeIcon icon={FiArrowLeft} />
              Back to Home
            </Link>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiShield} className="text-white text-xl" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Privacy Policy</h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                Last updated: January 2025
              </p>
            </motion.div>
          </div>

          {/* Content */}
          <Card className="prose prose-gray dark:prose-invert max-w-none">
            <div className="space-y-8">
              {/* Introduction */}
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <SafeIcon icon={FiEye} className="text-primary-500" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 m-0">Our Commitment to Your Privacy</h2>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  At Chore Haven, we take your family's privacy seriously. This Privacy Policy explains how we collect, 
                  use, disclose, and safeguard your information when you use our family chore management application.
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  We are committed to protecting your family's data and being transparent about our practices. 
                  This policy applies to all users of Chore Haven, including parents, guardians, and children.
                </p>
              </section>

              {/* Information We Collect */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <SafeIcon icon={FiDatabase} className="text-primary-500" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 m-0">1. Information We Collect</h3>
                </div>
                
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Account Information</h4>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  When you create a Chore Haven account, we collect:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300 mb-4">
                  <li>Email address (for account login and communications)</li>
                  <li>Password (encrypted and securely stored)</li>
                  <li>Family name (for your family profile)</li>
                </ul>

                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Family Profile Data</h4>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  To provide our family management features, we collect:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300 mb-4">
                  <li>Children's names and ages (optional)</li>
                  <li>Avatar/emoji selections for family members</li>
                  <li>Family preferences and settings</li>
                </ul>

                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Usage Data</h4>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  To improve our service and provide analytics, we collect:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300 mb-4">
                  <li>Tasks created, completed, and their details</li>
                  <li>Rewards set up and redeemed</li>
                  <li>Points earned and progress tracking</li>
                  <li>App usage patterns and feature interactions</li>
                  <li>Device information and browser type</li>
                </ul>

                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Information We Do NOT Collect</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                  <li>Real names or personal identifiers of children (only nicknames/display names)</li>
                  <li>Location data or GPS tracking</li>
                  <li>Photos or images of family members</li>
                  <li>Financial or payment information</li>
                  <li>Social security numbers or government IDs</li>
                  <li>School or address information</li>
                </ul>
              </section>

              {/* How We Use Information */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">2. How We Use Your Information</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                  <li><strong>Provide our Service:</strong> Enable task management, progress tracking, and family coordination</li>
                  <li><strong>Personalization:</strong> Customize your family's experience and show relevant content</li>
                  <li><strong>Communication:</strong> Send important updates, notifications, and support messages</li>
                  <li><strong>Improvement:</strong> Analyze usage patterns to enhance features and user experience</li>
                  <li><strong>Security:</strong> Protect against fraud, abuse, and security threats</li>
                  <li><strong>Legal Compliance:</strong> Meet our legal obligations and enforce our Terms of Service</li>
                </ul>
                
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    <strong>Child Safety Note:</strong> We never use children's data for marketing purposes or 
                    share it with third parties for advertising. Children's information is used solely to 
                    provide the chore management features within your family account.
                  </p>
                </div>
              </section>

              {/* Information Sharing */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <SafeIcon icon={FiUsers} className="text-primary-500" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 m-0">3. How We Share Information</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  <strong>We do not sell, rent, or trade your personal information.</strong> We may share your 
                  information only in the following limited circumstances:
                </p>
                
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Service Providers</h4>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  We work with trusted third-party service providers who help us operate our service:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300 mb-4">
                  <li>Supabase (database hosting and authentication)</li>
                  <li>Hosting and infrastructure providers</li>
                  <li>Email delivery services (for account notifications)</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  These providers are contractually obligated to protect your information and use it only 
                  for the services they provide to us.
                </p>

                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Legal Requirements</h4>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  We may disclose your information if required by law, such as in response to a court order, 
                  subpoena, or other legal process, or to protect the rights and safety of our users.
                </p>

                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Business Transfers</h4>
                <p className="text-gray-700 dark:text-gray-300">
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred 
                  as part of that transaction. We will notify you of any such change in ownership or control.
                </p>
              </section>

              {/* Data Security */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <SafeIcon icon={FiLock} className="text-primary-500" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 m-0">4. Data Security</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  We implement industry-standard security measures to protect your information:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                  <li><strong>Encryption:</strong> All data is encrypted in transit and at rest</li>
                  <li><strong>Access Controls:</strong> Strict access controls limit who can view your data</li>
                  <li><strong>Regular Audits:</strong> We regularly review our security practices</li>
                  <li><strong>Secure Infrastructure:</strong> We use reputable, secure hosting providers</li>
                  <li><strong>Password Protection:</strong> Passwords are hashed and never stored in plain text</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  While we implement strong security measures, no system is 100% secure. We encourage you to 
                  use strong, unique passwords and report any suspicious activity immediately.
                </p>
              </section>

              {/* Your Rights and Choices */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">5. Your Rights and Choices</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  You have the following rights regarding your personal information:
                </p>
                
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Access and Portability</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300 mb-4">
                  <li>View all data associated with your account through the app</li>
                  <li>Export your family's data in a portable format</li>
                  <li>Request a copy of your information by contacting us</li>
                </ul>

                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Correction and Updates</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300 mb-4">
                  <li>Update your account information and family profiles anytime</li>
                  <li>Correct any inaccurate information in your account</li>
                  <li>Modify privacy settings and preferences</li>
                </ul>

                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Deletion</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300 mb-4">
                  <li>Delete your entire account and all associated data</li>
                  <li>Remove individual family members from your account</li>
                  <li>Request deletion of specific information by contacting us</li>
                </ul>

                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Communication Preferences</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                  <li>Opt out of non-essential email communications</li>
                  <li>Customize notification settings within the app</li>
                  <li>Unsubscribe from marketing emails (if any)</li>
                </ul>
              </section>

              {/* Children's Privacy */}
              <section>
                <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg border border-green-200 dark:border-green-800">
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-3">6. Children's Privacy (COPPA Compliance)</h3>
                  <p className="text-green-700 dark:text-green-300 mb-3">
                    We are committed to protecting children's privacy in accordance with the Children's Online 
                    Privacy Protection Act (COPPA):
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-green-700 dark:text-green-300 mb-4">
                    <li>We do not knowingly collect personal information from children under 13 without parental consent</li>
                    <li>Parents have full control over their children's profiles and data</li>
                    <li>Children cannot create accounts independently - only parents can add them to family accounts</li>
                    <li>We do not use children's information for marketing or advertising purposes</li>
                    <li>Parents can review, modify, or delete their children's information at any time</li>
                  </ul>
                  <p className="text-green-700 dark:text-green-300">
                    If you believe we have collected information from a child under 13 without proper consent, 
                    please contact us immediately at privacy@chorehaven.com.
                  </p>
                </div>
              </section>

              {/* Data Retention */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">7. Data Retention</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  We retain your information for as long as necessary to provide our services and for legitimate 
                  business purposes:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                  <li><strong>Active Accounts:</strong> Data is retained while your account is active</li>
                  <li><strong>Deleted Accounts:</strong> Data is permanently deleted within 30 days of account deletion</li>
                  <li><strong>Backup Data:</strong> Backup copies are securely deleted within 90 days</li>
                  <li><strong>Legal Requirements:</strong> Some data may be retained longer if required by law</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  You can request immediate deletion of your data by contacting us directly.
                </p>
              </section>

              {/* International Users */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">8. International Users</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  Chore Haven is operated from [Your Country]. If you are using our service from outside 
                  [Your Country], please be aware that your information may be transferred to, stored, and 
                  processed in [Your Country] where our servers are located.
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  By using our service, you consent to the transfer of your information to [Your Country] 
                  and agree that such transfer is subject to this Privacy Policy.
                </p>
              </section>

              {/* Updates to Privacy Policy */}
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">9. Updates to This Privacy Policy</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or 
                  for legal, regulatory, or operational reasons. We will:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300 mb-4">
                  <li>Post the updated Privacy Policy on this page</li>
                  <li>Update the "Last updated" date at the top</li>
                  <li>Notify you of significant changes via email or app notification</li>
                  <li>For material changes affecting children's privacy, obtain renewed parental consent</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  Your continued use of Chore Haven after any changes indicates your acceptance of the updated Privacy Policy.
                </p>
              </section>

              {/* Contact Information */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <SafeIcon icon={FiMail} className="text-primary-500" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 m-0">10. Contact Us</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or your 
                  personal information, please contact us:
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    <strong>Privacy Officer Email:</strong> privacy@chorehaven.com
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    <strong>General Support:</strong> support@chorehaven.com
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    <strong>Website:</strong> https://chorehaven.com/contact
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Response Time:</strong> We respond to privacy inquiries within 48 hours
                  </p>
                </div>
              </section>

              {/* Footer Note */}
              <section className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg">
                  <p className="text-primary-800 dark:text-primary-200 text-sm text-center">
                    <strong>Our Promise:</strong> We are committed to protecting your family's privacy and being 
                    transparent about our data practices. Your trust is essential to our mission of making 
                    family chore management fun and effective. 🏠💙
                  </p>
                </div>
              </section>
            </div>
          </Card>

          {/* Navigation */}
          <div className="mt-8 text-center">
            <Link to="/">
              <Button variant="primary">
                Return to Chore Haven
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default PrivacyPolicyPage